/**
 * SocialDeepDive Plugin
 *
 * Enables detailed breakdown of social media metrics across 12 different platforms.
 * Provides platform-level tracking of followers, impressions, and engagements with
 * weighted average CPM calculations.
 *
 * Features:
 * - 12 platform support (Instagram, Facebook, YouTube, TikTok, LinkedIn, etc.)
 * - Platform selection per organization
 * - Weighted average CPM calculations
 * - Seamless integration with valuation forms
 */

import { Plugin, PluginContext } from '@/lib/platform/types/plugins';
import { pluginLogger } from '@/lib/platform/utils/logger';

export interface SocialDeepDiveConfig {
  enabled_platforms: string[]; // Array of platform IDs
}

export const socialDeepDivePlugin: Plugin<SocialDeepDiveConfig> = {
  manifest: {
    id: 'social-deepdive',
    name: 'SocialDeepDive for Valuation',
    version: '1.0.0',
    minSystemVersion: '1.0.0',
    pluginApiVersion: '1.0',
    description:
      'Go deep into your social media metrics across all platforms. Track followers, impressions, and engagements per platform with automatic weighted averages.',
    author: 'ValiYou',
    homepage: 'https://valiyou.com/plugins/social-deepdive',
  },

  capabilities: {
    requiredEndpoints: ['valuations'],
    requiredPermissions: ['read:valuations', 'write:valuations'],

    features: {
      reports: false,
      dashboard: false,
      valuations: true, // Integrated into valuation forms
      analytics: false,
      exports: false,
    },

    resourceLimits: {
      maxExecutionTimeMs: 5000, // 5 seconds
      maxApiCallsPerMinute: 120,
    },
  },

  lifecycle: {
    async onInstall() {
      pluginLogger.log('[SocialDeepDive] Plugin installed');
    },

    async onUninstall() {
      pluginLogger.log('[SocialDeepDive] Plugin uninstalled');
    },

    async onEnable() {
      pluginLogger.log('[SocialDeepDive] Plugin enabled');
    },

    async onDisable() {
      pluginLogger.log('[SocialDeepDive] Plugin disabled');
    },

    async healthCheck() {
      const config = socialDeepDivePlugin.currentConfig;

      if (!config) {
        return {
          status: 'degraded',
          message: 'No platforms configured',
          lastCheck: new Date(),
        };
      }

      const enabledCount = config.enabled_platforms?.length || 0;

      if (enabledCount === 0) {
        return {
          status: 'degraded',
          message: 'No platforms enabled. Configure platforms in settings.',
          lastCheck: new Date(),
        };
      }

      return {
        status: 'healthy',
        message: `${enabledCount} platform${enabledCount !== 1 ? 's' : ''} configured`,
        lastCheck: new Date(),
      };
    },
  },

  enabled: false,

  settings: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    component: null as any, // Will be set by PluginSection component
    schema: {
      type: 'object',
      properties: {
        enabled_platforms: {
          type: 'array',
          items: { type: 'string' },
        },
      },
      required: ['enabled_platforms'],
    },
    defaultConfig: {
      enabled_platforms: ['instagram', 'facebook', 'youtube', 'tiktok'],
    },
  },

  async initialize(context: PluginContext) {
    pluginLogger.log('[SocialDeepDive] Initializing plugin', {
      organizationId: context.organizationId,
    });

    this.context = context;

    // Load config from storage
    const config = await context.storage.get<SocialDeepDiveConfig>('config');
    if (config) {
      this.currentConfig = config;
      pluginLogger.log('[SocialDeepDive] Loaded config from storage', {
        platformCount: config.enabled_platforms?.length || 0,
      });
    } else {
      // Use default config
      this.currentConfig = this.settings?.defaultConfig;
      pluginLogger.log('[SocialDeepDive] Using default config');
    }
  },

  async cleanup() {
    pluginLogger.log('[SocialDeepDive] Cleaning up plugin');
    this.context = undefined;
    this.currentConfig = undefined;
  },
};
